import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addToCart,
  registerCheckout,
  fetchCheckouts,
  fetchProductDetail,
  selectIsUserLoggedIn,
  selectUserInfo,
  selectWithcookieData,
  selectProductDetailState
} from '../redux/WithcookieSlice';
import { showToast } from './Toast';
import './MembershipSignupComponent.css';
import { useTranslation } from '../redux/TranslationHelper';





const CustomMembershipSignupComponent = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // props와 componentProps 병합
  const {
    productId: propProductId = 0,
    title: propTitle = '',
    description: propDescription = '',
    buttonText: propButtonText = '',
    completedText: propCompletedText = '',
    showRefundVisualization: propShowRefundVisualization = true,
    refundLimitPercent: propRefundLimitPercent = 400,
    assetOptions: propAssetOptions = [],
    minAmount: propMinAmount = 10000,
    className: propClassName = 'membership-signup-wrapper',
    style: propStyle = {},
    isPreview: propIsPreview = false,
    componentProps = {},
  } = props;

  const {
    productId = propProductId,
    title = propTitle,
    description = propDescription,
    buttonText = propButtonText,
    completedText = propCompletedText,
    showRefundVisualization = propShowRefundVisualization,
    refundLimitPercent = propRefundLimitPercent,
    assetOptions = propAssetOptions,
    minAmount = propMinAmount,
    className = propClassName,
    style = propStyle,
    isPreview = propIsPreview,
  } = componentProps;

  // Redux 상태
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const userInfo = useSelector(selectUserInfo);
  const withcookieData = useSelector(selectWithcookieData);
  const { productData, loading: productLoading, error: productError } = useSelector(selectProductDetailState);

  // 상태
  const [amount, setAmount] = useState('');
  const [assetInputs, setAssetInputs] = useState({}); // { assetId: 금액 }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refundPercent, setRefundPercent] = useState(0);
  const [refundPercentOf, setRefundPercentOf] = useState(0);  
  const [totalReturnValue, setTotalReturnValue] = useState(0);
  const [totalPV, setTotalPV] = useState(0);
  const [checkouts, setCheckouts] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);

  // 프리뷰용 상태도 항상 선언
  const [previewAmount, setPreviewAmount] = useState('');
  const [previewAssetInputs, setPreviewAssetInputs] = useState({});
  const [previewCompleted, setPreviewCompleted] = useState(false);
  const [previewRefundPercent, setPreviewRefundPercent] = useState(0);
  const [previewTotalReturnValue, setPreviewTotalReturnValue] = useState(120000);
  const [previewTotalPV, setPreviewTotalPV] = useState(30000);

  // 사용자 자산 정보 로컬 상태
  const [localBalances, setLocalBalances] = useState([]);
  const [localBalancesLoading, setLocalBalancesLoading] = useState(false);
  const [localBalancesError, setLocalBalancesError] = useState(null);

  // 프리뷰 환급 퍼센트 계산 useEffect도 항상 선언
  useEffect(() => {
    if (!isPreview) return;
    let percent = 0;
    if (previewTotalPV > 0 && refundLimitPercent > 0) {
      percent = Math.round((previewTotalReturnValue / previewTotalPV) * 10000 / refundLimitPercent) / 100;
    }
    setPreviewRefundPercent(percent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewTotalReturnValue, previewTotalPV, refundLimitPercent, isPreview]);

  // 상품 정보 조회
  useEffect(() => {
    if (productId) {
      dispatch(fetchProductDetail(productId));
    }
  }, [dispatch, productId]);

  // 자산 정보 불러오기 (API 경로 수정)
  useEffect(() => {
    const fetchBalances = async () => {
      setLocalBalancesLoading(true);
      setLocalBalancesError(null);
      try {
        // AssetHistoryComponent 참조: 실제 자산 API 경로로 수정
        const response = await fetch('/api/assets/user/balances', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error('자산 정보를 불러오지 못했습니다.');
        const data = await response.json();
        setLocalBalances(data.assetBalances || []);
      } catch (err) {
        setLocalBalancesError(err.message || '자산 정보를 불러오지 못했습니다.');
      } finally {
        setLocalBalancesLoading(false);
      }
    };
    fetchBalances();
  }, []);

  // 주문 내역 가져오기 (의존성 최소화)
  useEffect(() => {
    if (!productId) return;
    setIsLoading(true);
    setError(null);
    dispatch(fetchCheckouts({ page: 1, take: 50 }))
      .unwrap()
      .then(response => {
        const allCheckouts = response && response.checkouts ? response.checkouts : [];
        // 해당 상품이 포함된 checkout만 필터
        const relevant = allCheckouts.filter(checkout =>
          checkout && checkout.carts && Array.isArray(checkout.carts) &&
          checkout.carts.some(cart => cart && cart.product && cart.product.id === productId)
        );
        setCheckouts(relevant);
        if (relevant.length === 0) {
          setTotalReturnValue(0);
          setTotalPV(0);
          setRefundPercent(0);
          setRefundPercentOf(0);
          setError(null); // 에러 메시지 초기화
          return;
        }
        // 환급 퍼센트 계산
        let returnSum = 0;
        let pvSum = 0;
        relevant.forEach(chk => {
          returnSum += Number(chk.returnValue || 0);
          pvSum += Number(chk.total_pv || 0);
        });
        setTotalReturnValue(returnSum);
        setTotalPV(pvSum);
        let percent = 0;
        let percentOf = 0;
        if (pvSum > 0 && refundLimitPercent > 0) {
          percentOf = Math.round((returnSum / pvSum) * 10000 / refundLimitPercent) / 100;
          percent = Math.round((returnSum / pvSum) * 100) / 100;
        }
        setRefundPercent(percent);
        setRefundPercentOf(percentOf);
        setError(null); // 정상 응답 시 에러 메시지 초기화
      })
      .catch(err => {
        console.log(err);
        setError(t('주문 내역을 불러오는데 실패했습니다.'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, productId]);

  // 금액 입력 핸들러 (총액 입력란 제거)
  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setAmount(value);
  };

  // 자산별 금액 입력 핸들러 (잔액 초과 제한)
  const handleAssetInputChange = (assetId, value, maxAmount) => {
    let cleanValue = value.replace(/[^0-9]/g, '');
    if (Number(cleanValue) > Number(maxAmount)) {
      cleanValue = maxAmount.toString();
    }
    setAssetInputs(prev => ({ ...prev, [assetId]: cleanValue }));
  };

  // 자산별 입력값 합계 계산
  const assetTotal = Object.entries(assetInputs).reduce((sum, [assetId, v]) => sum + Number(v || 0), 0);

  // 디버깅용 콘솔 출력
  console.log('assetInputs:', assetInputs);
  console.log('assetTotal:', assetTotal);
  console.log('minAmount:', minAmount);
  console.log('isLoading:', isLoading);

  // 결제 처리 (assetInputs만 사용, 총액 입력란 제거)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!isUserLoggedIn) {
      showToast(t('로그인이 필요합니다.'), 'warning');
      return;
    }
    
    // 상품 정보 - API에서 이미 받은 응답 사용
    const product = productData?.product;
    if (!product) {
      showToast(t('상품 정보를 불러올 수 없습니다.'), 'error');
      return;
    }
    
    if (assetTotal < minAmount) {
      setError(t(`최소 가입 가능 금액은 ${minAmount.toLocaleString()}$ 입니다.`));
      return;
    }
    // 자산별 결제 금액 합산
    let payments = [];
    localBalances.forEach(opt => {
      const v = parseInt(assetInputs[opt.assetTypeId] || '0', 10);
      if (v > 0) {
        payments.push({ method: 'ASSET', assetTypeId: opt.assetTypeId, amount: v });
      }
    });
    if (payments.length === 0) {
      setError(t('결제 수단을 입력하세요.'));
      return;
    }
    setIsLoading(true);
    try {
      const cartRes = await dispatch(addToCart({
        productId: product.id,
        count: 1,
        options: { custom_price: assetTotal }
      })).unwrap();
      const cartId = cartRes && cartRes.cart && cartRes.cart.id ? cartRes.cart.id : null;
      if (!cartId) throw new Error('장바구니 추가 실패');
      
      // 지불 방법 결정 (자산 유형이 여러 개면 'MIXED' 아니면 'ASSET')
      const paymentMethod = payments.length > 1 ? 'MIXED' : 'ASSET';
      
      const checkoutData = {
        // cart_ids와 carts 둘 다 제공
        cart_ids: [cartId],
        carts: [
          {
            product_id: product.id,
            company_id: withcookieData?.company?.id || userInfo.companyId || 19,
            user_id: userInfo.id,
            count: 1
          }
        ],
        payments,
        user_id: userInfo.id,
        company_id: withcookieData?.company?.id || userInfo.companyId || 19,
        skin_id: withcookieData?.skin?.id || 1,
        receiver_name: userInfo.name || '',
        receiver_phone: userInfo.phone || '',
        receiver_email: userInfo.email || '',
        address: userInfo.address || '',
        address_detail: '',
        postal_code: '',
        // 금액 정보 추가
        total_price: assetTotal,
        buy_price: assetTotal,
        parcel_price: 0,
        // 결제 방법 추가
        payment_method: paymentMethod
      };
      console.log('Checkout data:', checkoutData); // 디버깅용 로그 추가
      
      const result = await dispatch(registerCheckout(checkoutData)).unwrap();
      console.log('Checkout result:', result); // 응답 로깅
      
      setIsCompleted(true);
      showToast(t('추가 결제가 완료되었습니다.'), 'success');
      setAssetInputs({});
      dispatch(fetchCheckouts({ page: 1, take: 100 }));
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Checkout error:', err); // 에러 상세 로깅
      setError(err?.message || t('결제 처리 중 오류가 발생했습니다.'));
      showToast(err?.message || t('결제 처리 중 오류가 발생했습니다.'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 미리보기 렌더링 (실제 입력/시각화/버튼 동작, API는 호출하지 않음)
  if (isPreview) {
    const handlePreviewAmountChange = (e) => {
      const value = e.target.value.replace(/[^0-9]/g, '');
      setPreviewAmount(value);
    };
    const handlePreviewAssetInputChange = (assetId, value) => {
      const cleanValue = value.replace(/[^0-9]/g, '');
      setPreviewAssetInputs(prev => ({ ...prev, [assetId]: cleanValue }));
    };
    const handlePreviewSubmit = (e) => {
      e.preventDefault();
      if (!previewAmount || parseInt(previewAmount, 10) < minAmount) {
        showToast(t(`최소 가입 가능 금액은 ${minAmount.toLocaleString()}$ 입니다.`), 'warning');
        return;
      }
      setPreviewCompleted(true);
      setPreviewAmount('');
      setPreviewAssetInputs({});
    };
    const renderPreviewRefundVisualization = () => {
      if (!showRefundVisualization) return null;
      return (
        <div className="refund-visualization">
          <div className="refund-bar-bg">
            <div className="refund-bar" style={{ width: `${Math.min(previewRefundPercent, 100)}%` }}></div>
          </div>
          <div className="refund-text">
            환급률: {previewRefundPercent}% / {refundLimitPercent}%<br />
            누적 환급액: {previewTotalReturnValue.toLocaleString()}$<br />
            누적 PV: {previewTotalPV.toLocaleString()}
          </div>
        </div>
      );
    };
    return (
      <div className={className} style={style}>
        <div className="container">
          <h2 className="membership-title">{title}</h2>
          <p className="membership-description">{description}</p>
          {renderPreviewRefundVisualization()}
          <form onSubmit={handlePreviewSubmit} className="custom-membership-form">
            <div className="input-group">
              <label>금액 입력</label>
              <input
                type="text"
                value={previewAmount}
                onChange={handlePreviewAmountChange}
                placeholder={`${minAmount.toLocaleString()}$ 이상 입력`}
              />
            </div>
            {assetOptions && assetOptions.length > 0 && (
              <div className="asset-inputs">
                <label>자산별 결제 금액</label>
                {assetOptions.map(opt => (
                  <div key={opt.id} className="asset-input-row">
                    <span>{opt.id}</span>
                    <input
                      type="text"
                      value={previewAssetInputs[opt.id] || ''}
                      onChange={e => handlePreviewAssetInputChange(opt.id, e.target.value)}
                      placeholder="금액 입력"
                    />
                  </div>
                ))}
              </div>
            )}
            <button
              className="signup-button"
              type="submit"
              disabled={!previewAmount || parseInt(previewAmount, 10) < minAmount}
            >
              {buttonText}
            </button>
          </form>
          {previewCompleted && (
            <div className="signup-completed">
              <p>{completedText}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isLoading || productLoading) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{t('처리 중입니다...')}</p>
          </div>
        </div>
      </div>
    );
  }

  if ((error || productError) && error !== null && error !== '' && checkouts && checkouts.length > 0) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="error-message">
            <p>{error || productError}</p>
            <button className="retry-button" onClick={() => window.location.reload()}>{t('다시 시도')}</button>
          </div>
        </div>
      </div>
    );
  }

  if (!isUserLoggedIn) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="login-required">
            <h2 className="membership-title">{title}</h2>
            <p>{t('멤버십 가입을 위해 로그인이 필요합니다.')}</p>
            <a href="/login" className="login-button">{t('로그인하기')}</a>
          </div>
        </div>
      </div>
    );
  }

  if (!productData || !productData.product) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="error-message">
            <h2 className="membership-title">{title}</h2>
            <p>{t('표시할 상품이 없습니다.')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (localBalancesLoading) {
    return <div className={className} style={style}><div className="container"><div className="error-message">자산 정보를 불러오는 중입니다...</div></div></div>;
  }
  if (localBalancesError) {
    return <div className={className} style={style}><div className="container"><div className="error-message">{localBalancesError}</div></div></div>;
  }
  if (!localBalances || localBalances.length === 0) {
    return <div className={className} style={style}><div className="container"><div className="error-message">{t('자산 정보를 불러올 수 없습니다.')}</div></div></div>;
  }

  if (!checkouts) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="error-message">
            <p>{t('구매내역이 없습니다.')}</p>
          </div>
        </div>
      </div>
    );
  }

  // 환급 퍼센트 시각화
  const renderRefundVisualization = () => {
    if (!showRefundVisualization) return null;
    return (
      <div className="refund-visualization">
        <div className="refund-bar-bg">
          <div className="refund-bar" style={{ width: `${Math.min(refundPercentOf * 100, 100)}%` }}></div>
        </div>
        <div className="refund-text">
          {t('환급률')}: {(refundPercent * 100).toFixed(1)}% / {refundLimitPercent}%<br />
          {t('누적 투자액')}: {totalPV.toLocaleString()}$<br />
          {t('누적 환급액')}: {totalReturnValue.toLocaleString()}$<br />
        </div>
      </div>
    );
  };

  return (
    <div className={className} style={style}>
      <div className="container">
        <h2 className="membership-title">{title}</h2>
        <p className="membership-description">{description}</p>
        {renderRefundVisualization && renderRefundVisualization()}
        <form onSubmit={handleSubmit} className="custom-membership-form">
          {localBalances && localBalances.length > 0 && (
            <div className="asset-inputs">
              {localBalances.map(asset =>
                asset.assetTypeId === 4 ? (
                  <div key={asset.assetTypeId} className="asset-input-row">
                    <input
                      type="text"
                      value={assetInputs[asset.assetTypeId] || ''}
                      onChange={e => handleAssetInputChange(asset.assetTypeId, e.target.value, asset.availableAmount)}
                      placeholder={`${t(asset.companyDisplayNames?.[asset.company?.id]?.displayName || asset.assetName)} (${t('balance')}: ${asset.availableAmount})`}
                      disabled={isLoading}
                    />
                  </div>
                ) : null
              )}
            </div>
          )}
          <div className="asset-total">
            {t('total')}: {assetTotal.toLocaleString()}$
          </div>
          <button
            className="signup-button"
            type="submit"
            disabled={isLoading || assetTotal < minAmount}
          >
            {t(buttonText)}
          </button>
        </form>
        {isCompleted && (
          <div className="signup-completed">
            <p>{t(completedText)}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// displayName 추가
CustomMembershipSignupComponent.displayName = '자유 멤버십 가입';

export default CustomMembershipSignupComponent; 